import { motion } from "framer-motion";
import { MapPin, Ruler, Wrench, GearSix, Buildings } from "@phosphor-icons/react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const projects = [
  {
    id: 1,
    title: "Warehouse F — Bulk24 Pty (Ltd)",
    category: "Structural Steel",
    location: "Richards Bay, KwaZulu-Natal",
    size: "1,600 m\u00B2",
    scope: "Demolition / Dismantling, Design, Fabrication & Erection",
    description: "Large-scale structural replacement of a 1,600 m\u00B2 warehouse. Integrated demolition of the existing structure, complete design of the new facility, precision fabrication of all structural steel components, and full on-site erection.",
    highlights: ["Full-cycle project management", "Seamless demolition-to-erection workflow", "Compliance with SABS engineering standards"],
    icon: <Buildings size={24} weight="duotone" />,
    color: "bg-[#00A091]",
    image: "/images/projects/warehouse-f.jpeg",
  },
  {
    id: 2,
    title: "Passage Warehouse — Sprint Logistics",
    category: "Structural Steel",
    location: "Richards Bay, KwaZulu-Natal",
    scope: "Design, Fabrication & Erection",
    description: "Constructed a new passage warehouse structure between two existing operational buildings. Innovative engineering approach utilizing adjacent columns as shared supports, minimizing disruption to ongoing logistics operations.",
    highlights: ["Integration with existing structures", "Minimal operational disruption", "Technical creativity in confined spaces"],
    icon: <Buildings size={24} weight="duotone" />,
    color: "bg-[#0F4C5C]",
    image: "/images/projects/passage-warehouse.jpeg",
  },
  {
    id: 3,
    title: "Warehouse D Repair — Bulk24 Pty (Ltd)",
    category: "Structural Steel",
    location: "Richards Bay, KwaZulu-Natal",
    scope: "Partial Demolition, Fabrication & Erection",
    description: "Emergency rapid response to structural failure. Partial demolition and dismantling of damaged section, fabrication of new replacement components, and seamless integration with the remaining sound portion of the structure.",
    highlights: ["Rapid emergency response", "Precision component matching", "Structural integrity restoration"],
    icon: <Wrench size={24} weight="duotone" />,
    color: "bg-[#00A091]",
    image: "/images/projects/warehouse-d.jpeg",
  },
  {
    id: 4,
    title: "Mobile Structure — Bulk24 Riverhorse Valley",
    category: "Custom Fabrication",
    location: "Riverhorse Valley, KwaZulu-Natal",
    size: "350 m\u00B2",
    scope: "Custom Design, Fabrication & Installation",
    description: "Custom-fabricated, fully mobile, and demountable steel structure. Designed as a warehouse extension for internal truck loading/offloading access. A non-permanent, relocatable solution solving a specific logistical challenge.",
    highlights: ["Fully mobile & demountable", "Custom logistical solution", "350 m\u00B2 functional space"],
    icon: <GearSix size={24} weight="duotone" />,
    color: "bg-[#0F4C5C]",
    image: "/images/projects/riverhorse-mobile-warehouse.jpeg",
  },
  {
    id: 5,
    title: "10TPH Cold Mix Asphalt Plant",
    category: "Specialty Plant",
    location: "Carletonville, Gauteng",
    scope: "Design, Fabrication & On-Site Installation",
    description: "Custom-designed 10 ton per hour Cold Mix Asphalt Plant. Fabricated as transportable modular components including aggregate feeding systems, bitumen emulsion tanks, mixing drums, and control systems. Fully commissioned on-site.",
    highlights: ["In-house custom design", "Modular fabrication", "On-site commissioning"],
    icon: <GearSix size={24} weight="duotone" />,
    color: "bg-[#00A091]",
    image: "/images/projects/cold-mix-plant.jpeg",
  },
  {
    id: 6,
    title: "Debagging Plant Upgrade",
    category: "Plant Refurbishment",
    location: "Confidential Client",
    scope: "Full Refurbishment, Safety & Electrical Upgrades",
    description: "Complete refurbishment of a bulk material debagging and handling plant. Included high-performance vibrator system, structural repair of hoppers, new motors, catwalks and platforms, safety systems (E-stops and sirens), new DB board, and full electrical overhaul.",
    highlights: ["Extended plant lifespan", "Safety system implementation", "Full electrical replacement"],
    icon: <Wrench size={24} weight="duotone" />,
    color: "bg-[#0F4C5C]",
    image: "/images/projects/sugar-debagging-unit.jpeg",
  },
];

export default function ProjectPortfolio() {
  return (
    <div>
      {/* HERO */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="portfolio-hero">
        <div className="absolute inset-0">
          <img src="/images/extracted/company_profile_p2_img5.jpeg" alt="KZNSC Projects" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B2D2D]/85" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">Our Work</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6">
              Project<br />Portfolio
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-[#E0F5F2]/70 max-w-2xl leading-relaxed">
              A showcase of our completed projects across structural steel fabrication, specialty plant design, and industrial equipment installation throughout Southern Africa.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* PROJECTS GRID */}
      <section className="py-20 lg:py-28 bg-white" data-testid="portfolio-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {projects.map((proj, i) => (
              <motion.div key={proj.id} variants={fadeUp} className="card-clean rounded-sm flex flex-col overflow-hidden" data-testid={`portfolio-project-${i}`}>
                {/* Project Image */}
                <div className="img-zoom h-[200px]">
                  <img src={proj.image} alt={proj.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 flex-1">
                  {/* Category + Location */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#00A091] font-bold bg-[#E0F5F2] px-3 py-1">{proj.category}</span>
                    <div className={`w-8 h-8 ${proj.color} text-white flex items-center justify-center`}>{proj.icon}</div>
                  </div>

                  <h3 className="font-heading font-bold text-xl text-[#0B2D2D] mb-2 leading-tight">{proj.title}</h3>

                  <div className="flex flex-wrap gap-3 mb-4">
                    <span className="flex items-center gap-1 text-xs text-[#0B2D2D]/50">
                      <MapPin size={12} weight="duotone" className="text-[#00A091]" />
                      {proj.location}
                    </span>
                    {proj.size && (
                      <span className="flex items-center gap-1 text-xs text-[#0B2D2D]/50">
                        <Ruler size={12} weight="duotone" className="text-[#00A091]" />
                        {proj.size}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-[#0B2D2D]/60 leading-relaxed mb-5">{proj.description}</p>

                  {/* Highlights */}
                  <div className="space-y-1.5">
                    {proj.highlights.map((h, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-[#A7D129] flex-shrink-0" />
                        <span className="text-xs text-[#0B2D2D]/70">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scope footer */}
                <div className="px-8 py-4 border-t border-[#0F4C5C]/10 bg-[#E0F5F2]/50">
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#0F4C5C]/60">{proj.scope}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0F4C5C] py-16 relative noise-overlay" data-testid="portfolio-stats">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "6+", label: "Major Projects" },
              { value: "3,550+", label: "Square Metres Built" },
              { value: "3", label: "Provinces Served" },
              { value: "100%", label: "Client Satisfaction" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <p className="font-heading font-black text-3xl md:text-4xl text-[#A7D129]">{s.value}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0F5F2]/60 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
